// client/src/hooks/useComunicacaoConfig.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const CONFIG_QUERY_KEY = 'comunicacaoConfig';

// 🔹 Buscar todas as configurações
export const useComunicacaoConfigs = (params, options) => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: [CONFIG_QUERY_KEY, params],
        queryFn: async () => {
            try {
                const { data } = await api.get('/comunicacao-config', { params });
                return data.data;
            } catch (error) {
                setAlert(error.response?.data?.error || 'Erro ao carregar configurações', 'danger');
                throw error;
            }
        },
        placeholderData: (previousData) => previousData,
        ...options,
    });
};

// 🔹 Buscar configuração específica
export const useComunicacaoConfig = (id) => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: [CONFIG_QUERY_KEY, id],
        queryFn: async () => {
            if (!id) return null;
            try {
                const { data } = await api.get(`/comunicacao-config/${id}`);
                return data.data;
            } catch (error) {
                setAlert(error.response?.data?.error || 'Erro ao carregar configuração', 'danger');
                throw error;
            }
        },
        enabled: !!id,
    });
};

// 🔹 Mutations (create, update, delete)
export const useMutateComunicacaoConfig = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCache = () => {
        queryClient.invalidateQueries({ queryKey: [CONFIG_QUERY_KEY] });
    };

    const createConfig = useMutation({
        mutationFn: async (data) => {
            const response = await api.post('/comunicacao-config', data);
            return response.data.data;
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Configuração criada com sucesso!', 'success');
        },
        onError: (err) => {
            const msg = err.response?.data?.error || 'Erro ao criar configuração';
            setAlert(msg, 'danger');
        },
    });

    const updateConfig = useMutation({
        mutationFn: async ({ id, data }) => {
            const response = await api.put(`/comunicacao-config/${id}`, data);
            return response.data.data;
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Configuração atualizada com sucesso!', 'success');
        },
        onError: (err) => {
            const msg = err.response?.data?.error || 'Erro ao atualizar configuração';
            setAlert(msg, 'danger');
        },
    });

    const deleteConfig = useMutation({
        mutationFn: async (id) => {
            await api.delete(`/comunicacao-config/${id}`);
            return id;
        },
        onSuccess: () => {
            invalidateCache();
            setAlert('Configuração removida com sucesso!', 'success');
        },
        onError: (err) => {
            const msg = err.response?.data?.error || 'Erro ao remover configuração';
            setAlert(msg, 'danger');
        },
    });

    return { createConfig, updateConfig, deleteConfig };
};